import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import env from '../../../../environments/environment'

import './YourAgencysFuture.component.scss'

const RECAPTCHA_V3_SITE_KEY = env.integrations.google_recaptcha.key
const API_BASE = env.api.base_url

const INITIAL_STATE = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  primaryMarket: '',
  state: '',
  taxableEarnings: '',
  numberOfEmployees: '',
  numberOfWritingAgents: '',
  hasPandLs: '',
  willingToStay: '',
}

const YourAgencysFuture = () => {
  const [formData, setFormData] = useState(INITIAL_STATE)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (document.querySelector(`script[src*="recaptcha/api.js"]`)) return

    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_V3_SITE_KEY}`
    script.async = true
    document.head.appendChild(script)
  }, [])

  const getRecaptchaToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!window.grecaptcha) {
        reject(new Error('reCAPTCHA not loaded'))
        return
      }
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(RECAPTCHA_V3_SITE_KEY, { action: 'contact_submission' }).then(resolve).catch(reject)
      })
    })
  }, [])

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (submitting) return

    setSubmitting(true)

    try {
      const recaptchaToken = await getRecaptchaToken()

      const response = await fetch(`${API_BASE}transitionYourAgencySubmission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, recaptchaToken }),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Submission failed')
      }

      setSubmitted(true)
      toast.success('Your inquiry has been submitted successfully.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } catch (error) {
      toast.error(error.message || 'Something went wrong. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className='your-agencys-future-section'>
      <div className='your-agencys-future-inner'>
        <h2>Let's Discuss Your Agency's Future</h2>
        <p className='section-description'>
          Whether you're ready to transition now or simply exploring your options, our leadership team is here to help
          you plan the next chapter with clarity and confidence.
        </p>

        {submitted ? (
          <div className='form-success-message'>
            <p>Thank you for your submission. Our team will be in touch shortly.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className='agency-future-form'>
            <div className='form-row'>
              <div className='form-field half'>
                <label>
                  First Name:<span className='required-asterisk'> *</span>
                </label>
                <input type='text' name='firstName' value={formData.firstName} onChange={handleChange} required />
              </div>
              <div className='form-field half'>
                <label>
                  Last Name:<span className='required-asterisk'> *</span>
                </label>
                <input type='text' name='lastName' value={formData.lastName} onChange={handleChange} required />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field half'>
                <label>
                  Email:<span className='required-asterisk'> *</span>
                </label>
                <input type='email' name='email' value={formData.email} onChange={handleChange} required />
              </div>
              <div className='form-field half'>
                <label>
                  Phone:<span className='required-asterisk'> *</span>
                </label>
                <input type='tel' name='phone' value={formData.phone} onChange={handleChange} required />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field half'>
                <label>
                  Primary Market:<span className='required-asterisk'> *</span>
                </label>
                <select name='primaryMarket' value={formData.primaryMarket} onChange={handleChange} required>
                  <option value=''></option>
                  <option value='ACA'>ACA</option>
                  <option value='Alternative U65'>Alternative U65</option>
                  <option value='Senior'>Senior</option>
                </select>
              </div>
              <div className='form-field half'>
                <label>
                  State:<span className='required-asterisk'> *</span>
                </label>
                <input type='text' name='state' value={formData.state} onChange={handleChange} required />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field full'>
                <label>
                  Taxable Earnings:<span className='required-asterisk'> *</span>
                </label>
                <input
                  type='text'
                  name='taxableEarnings'
                  value={formData.taxableEarnings}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field half'>
                <label>
                  Number Of Employees:
                  <span className='required-asterisk'> *</span>
                </label>
                <input
                  type='text'
                  name='numberOfEmployees'
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className='form-field half'>
                <label>
                  Number Of Writing Agents?
                  <span className='required-asterisk'> *</span>
                </label>
                <input
                  type='text'
                  name='numberOfWritingAgents'
                  value={formData.numberOfWritingAgents}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className='form-row'>
              <div className='form-field half'>
                <label>
                  Do You Have 2-3 Years Of P&amp;Ls?
                  <span className='required-asterisk'> *</span>
                </label>
                <select name='hasPandLs' value={formData.hasPandLs} onChange={handleChange} required>
                  <option value=''></option>
                  <option value='Yes'>Yes</option>
                  <option value='No'>No</option>
                </select>
              </div>
              <div className='form-field half'>
                <label>
                  Are You Willing To Stay On For 3 Years?
                  <span className='required-asterisk'> *</span>
                </label>
                <select name='willingToStay' value={formData.willingToStay} onChange={handleChange} required>
                  <option value=''></option>
                  <option value='Yes'>Yes</option>
                  <option value='No'>No</option>
                </select>
              </div>
            </div>

            <div className='form-submit-row'>
              <button type='submit' disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  )
}

export default YourAgencysFuture
